import { useState, useEffect } from "react";


/*  Constants  */
export const MAX_VIDEO_SIZE_MB = 200;
export const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;
export const ALLOWED_VIDEO_EXTENSIONS = ["mp4", "webm", "ogg", "mov", "m4v", "avi", "mkv"];

/*  Duration helpers ── */

export const isValidDuration = (value) => {
  if (!value) return false;
  const t = String(value).trim();
  if (!t || t === "0:00" || t === "00:00") return false;
  return /^\d+:\d{2}(:\d{2})?$/.test(t);
};

export const formatDuration = (totalSeconds) => {
  const s = Math.round(Number(totalSeconds) || 0);
  if (s <= 0) return null;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

/*  YouTube / Vimeo helpers ─ */

export const getYoutubeId = (url = "") => {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/
  );
  return match?.[1] || null;
};

export const getVimeoId = (url = "") => {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match?.[1] || null;
};

export const detectThumbnailFromUrl = (url) => {
  if (!url) return null;
  const yt = getYoutubeId(url);
  if (yt) return `https://img.youtube.com/vi/${yt}/hqdefault.jpg`;
  return null;
};

/**
 * Fetch thumbnail + duration from remote video URL.
 * Supports YouTube (thumbnail from API) and Vimeo (oEmbed).
 * @param {string} url
 * @returns {Promise<{ thumbnail: string|null, duration: string|null }>}
 */
export const resolveUrlVideoMeta = async (url) => {
  let thumbnail = detectThumbnailFromUrl(url);
  let duration = null;

  const vimeoId = getVimeoId(url);
  if (vimeoId) {
    try {
      const res = await fetch(
        `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`
      );
      if (res.ok) {
        const json = await res.json();
        if (json.thumbnail_url) thumbnail = json.thumbnail_url;
        if (json.duration) duration = formatDuration(json.duration);
      }
    } catch (_) {
      // Non-fatal — just return what we have
    }
  }

  return { thumbnail, duration };
};

/**
 * Extract duration and thumbnail from a local File object using the browser <video> element.
 * @param {File} file
 * @returns {Promise<{ duration: string|null, thumbnail: string|null }>}
 */
export const extractMetaFromFile = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;

    const cleanup = () => URL.revokeObjectURL(url);

    video.addEventListener("loadedmetadata", () => {
      const dur = formatDuration(video.duration);
      // Seek to 1s (or middle) to capture a meaningful thumbnail
      video.currentTime = Math.min(1, video.duration / 2);
    });

    video.addEventListener("seeked", () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 180;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnail = canvas.toDataURL("image/jpeg", 0.7);
        cleanup();
        resolve({ duration: formatDuration(video.duration), thumbnail });
      } catch (_) {
        cleanup();
        resolve({ duration: formatDuration(video.duration), thumbnail: null });
      }
    });

    video.addEventListener("error", () => {
      cleanup();
      resolve({ duration: null, thumbnail: null });
    });

    video.src = url;
  });

/**
 * Simple debounce hook helper — returns a debounced value.
 * Used to delay URL meta-fetch on each keystroke.
 */
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};
